import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Building2,
  Globe,
  Loader2,
  X,
  Recycle,
  Package,
  Ruler,
} from 'lucide-react';
import inventoryService from '../services/inventoryService';
import leftoverService from '../services/leftoverService';
import Layout from '../components/Layout';
import AdvancedSearch from '../components/AdvancedSearch';
import InventoryOverview from '../components/InventoryOverview';
import InventoryList from '../components/InventoryList';
import LeftoverInventoryList from '../components/LeftoverInventoryList';
import AccessibilityWrapper from '../components/AccessibilityWrapper';
import InventoryHistoryDialog from '../components/InventoryHistoryDialog';
import ClientSelector from '../components/ClientSelector';
import useDebounce from '../hooks/useDebounce';

const InventoryPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [excludeZeroStock, setExcludeZeroStock] = useState(true);
  const [stats, setStats] = useState(null);
  const isInitialMount = useRef(true);
  // Client context state
  const [selectedClientId, setSelectedClientId] = useState(() => {
    // Initialize from localStorage or default to global
    return localStorage.getItem('inventorySelectedClient') || 'global';
  });
  const [selectedClient, setSelectedClient] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_count: 0,
    total_pages: 0
  });
  const [filters, setFilters] = useState(() => {
    // Initialize product_id from URL params synchronously
    const urlParams = new URLSearchParams(window.location.search);
    return {
      name: '',
      category: '',
      product_id: urlParams.get('product_id') || '',
      product_code: '',  // Separate filter for product_code field in warehouse_movements
      part_number: '',
      client_ref_number: '',
      material: '',
      coating: '',
      stock_status: '',
      price_range: { min: '', max: '' },
      tip_diameter_range: { min: '', max: '' },
      total_length_range: { min: '', max: '' },
      degree_range: { min: '', max: '' },
      // Inventory-specific filters
      min_stock: '',
      max_stock: '',
      location: ''
    };
  });
  const [sort, setSort] = useState({ by: 'name', order: 'asc' });
  const [viewMode, setViewMode] = useState('finished'); // 'finished' | 'raw_material' | 'leftover'
  const [searchMode, setSearchMode] = useState('basic'); // 'basic' or 'advanced'
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');

  // History dialog state
  const [historyDialog, setHistoryDialog] = useState({ open: false, product: null });
  const [leftoverDialog, setLeftoverDialog] = useState({ open: false, product: null });
  const [leftovers, setLeftovers] = useState([]);
  const [leftoverLoading, setLeftoverLoading] = useState(false);
  const [leftoverError, setLeftoverError] = useState('');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  // Debounce global search for better performance
  const debouncedGlobalSearch = useDebounce(globalSearch, 300);

  const loadInventory = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      let response;
      const currentClientId = selectedClientId === 'global' ? null : selectedClientId;

      if (viewMode === 'leftover') {
        // Leftover inventory mode
        const leftoverFilters = {
          page: pagination.page,
          page_size: pagination.page_size,
          sort_by: sort.by === 'name' ? 'created_at' : sort.by,
          sort_order: sort.order,
          search: filters.name || undefined,
          parent_product_id: filters.product_id || undefined,
          tip_diameter: filters.tip_diameter_range?.min ? parseFloat(filters.tip_diameter_range.min) : undefined,
          min_length: filters.total_length_range?.min ? parseFloat(filters.total_length_range.min) : undefined,
          max_length: filters.total_length_range?.max ? parseFloat(filters.total_length_range.max) : undefined,
          location: filters.location || undefined
        };
        response = await leftoverService.findAvailable(leftoverFilters);
        // Normalize response format to match inventory API
        setInventory(response.leftovers || []);
        setPagination(response.pagination || {
          page: pagination.page,
          page_size: pagination.page_size,
          total_count: response.count || 0,
          total_pages: Math.ceil((response.count || 0) / pagination.page_size)
        });
        return;
      }

      if (searchMode === 'advanced' || debouncedGlobalSearch.trim()) {
        // Use advanced search when global search is active or in advanced mode
        const searchCriteria = {
          query: debouncedGlobalSearch.trim(),
          filters: {
            ...filters,
            // Convert price range to proper format
            price_min: filters.price_range.min ? parseFloat(filters.price_range.min) : undefined,
            price_max: filters.price_range.max ? parseFloat(filters.price_range.max) : undefined,
            // Manufacturing parameter range filters
            tip_diameter_min: filters.tip_diameter_range?.min ? parseFloat(filters.tip_diameter_range.min) : undefined,
            tip_diameter_max: filters.tip_diameter_range?.max ? parseFloat(filters.tip_diameter_range.max) : undefined,
            total_length_min: filters.total_length_range?.min ? parseFloat(filters.total_length_range.min) : undefined,
            total_length_max: filters.total_length_range?.max ? parseFloat(filters.total_length_range.max) : undefined,
            degree_min: filters.degree_range?.min ? parseFloat(filters.degree_range.min) : undefined,
            degree_max: filters.degree_range?.max ? parseFloat(filters.degree_range.max) : undefined,
            exclude_zero_stock: excludeZeroStock
          },
          sort: { by: sort.by, order: sort.order },
          page: pagination.page,
          page_size: pagination.page_size
        };

        // Remove empty filters
        Object.keys(searchCriteria.filters).forEach(key => {
          if (!searchCriteria.filters[key] && searchCriteria.filters[key] !== 0) {
            delete searchCriteria.filters[key];
          }
        });

        response = await inventoryService.advancedSearch(searchCriteria, currentClientId);
      } else {
        // Use basic search for standard filtering
        // Include reservation details for reserved stock indicator
        response = await inventoryService.getInventory(
          pagination.page,
          pagination.page_size,
          sort.by,
          sort.order,
          { ...filters, include_reservations: true },
          currentClientId,
          excludeZeroStock,
          viewMode // 'finished' or 'raw_material'
        );
      }

      setInventory(response.inventory || []);
      setPagination(prev => ({
        ...prev,
        ...response.pagination
      }));
    } catch (err) {
      setError(err.response?.data?.error || '加载库存失败');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.page_size, sort.by, sort.order, filters, searchMode, debouncedGlobalSearch, selectedClientId, excludeZeroStock, viewMode]);

  const loadCategories = useCallback(async () => {
    try {
      const response = await inventoryService.getCategories();
      setCategories(response.categories);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const currentClientId = selectedClientId === 'global' ? null : selectedClientId;
      const response = await inventoryService.getInventoryStats(currentClientId);
      setStats(response);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, [selectedClientId]);

  // Load data on mount and when key dependencies change
  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only load categories once on mount

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Clear product_id from URL after initial load (already read in useState initializer)
  useEffect(() => {
    const urlProductId = searchParams.get('product_id');
    if (urlProductId) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('product_id');
      setSearchParams(newParams, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  // Reset page to 1 when filters or globalSearch change
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [filters, globalSearch]);

  // Client selection handlers
  const handleClientChange = useCallback((clientId, clientData) => {
    const newClientId = clientId || 'global';
    setSelectedClientId(newClientId);
    setSelectedClient(clientData);

    // Persist client selection
    if (newClientId === 'global') {
      localStorage.removeItem('inventorySelectedClient');
    } else {
      localStorage.setItem('inventorySelectedClient', newClientId);
    }

    // Reset pagination to first page when switching clients
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Enhanced filter change for nested objects
  const handleAdvancedFilterChange = (filterName, value) => {
    if (filterName.includes('.')) {
      // Handle nested filters like 'price_range.min'
      const [parent, child] = filterName.split('.');
      setFilters(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [filterName]: value
      }));
    }
  };

  // Global search handler with debouncing
  const handleGlobalSearchChange = useCallback(
    async (searchValue) => {
      setGlobalSearch(searchValue);

      if (searchValue.trim().length >= 2) {
        setSearchLoading(true);
        try {
          // Get search suggestions
          const response = await inventoryService.searchInventory(searchValue, 1, 5);
          const suggestions = response.products?.map(p => ({
            label: `${p.name} (${p.product_id})`,
            value: p.name,
            productId: p.product_id,
            category: p.category
          })) || [];
          setSearchSuggestions(suggestions);
        } catch (err) {
          console.error('Search suggestions failed:', err);
          setSearchSuggestions([]);
        } finally {
          setSearchLoading(false);
        }
      } else {
        setSearchSuggestions([]);
      }
    },
    []
  );

  const handleFilterSubmit = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilter = (filterName) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: ''
    }));
  };

  const handleClearAllFilters = useCallback(() => {
    setFilters({
      name: '',
      category: '',
      product_id: '',
      product_code: '',
      part_number: '',
      client_ref_number: '',
      material: '',
      coating: '',
      stock_status: '',
      price_range: { min: '', max: '' },
      tip_diameter_range: { min: '', max: '' },
      total_length_range: { min: '', max: '' },
      degree_range: { min: '', max: '' },
      min_stock: '',
      max_stock: '',
      location: ''
    });
    setGlobalSearch('');
    setSearchSuggestions([]);
  }, []);

  // Export inventory functionality
  const handleExportInventory = async () => {
    try {
      await inventoryService.exportInventoryList(inventory, 'csv');
    } catch (err) {
      setError('导出库存失败');
    }
  };

  // Toggle search modes
  const handleSearchModeToggle = () => {
    setSearchMode(prev => prev === 'basic' ? 'advanced' : 'basic');
    if (searchMode === 'advanced') {
      setAdvancedFiltersOpen(false);
      setGlobalSearch('');
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleSort = useCallback((property) => {
    const isAsc = sort.by === property && sort.order === 'asc';
    setSort({ by: property, order: isAsc ? 'desc' : 'asc' });
  }, [sort.by, sort.order]);

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
    setPagination(prev => ({ ...prev, page: 1 }));
    // Reset sort to a sensible default for the new mode
    setSort(mode === 'leftover' ? { by: 'created_at', order: 'desc' } : { by: 'name', order: 'asc' });
    handleClearAllFilters();
  }, [handleClearAllFilters]);

  const handleViewHistory = useCallback((product) => {
    // Open history dialog for the selected product
    setHistoryDialog({ open: true, product });
  }, []);

  // History dialog close handler
  const handleHistoryDialogClose = useCallback(() => {
    setHistoryDialog({ open: false, product: null });
  }, []);

  const handleViewLeftovers = useCallback(async (product) => {
    setLeftoverDialog({ open: true, product });
    setLeftoverLoading(true);
    setLeftoverError('');
    setLeftovers([]);

    try {
      const parentId = product.product_code || product.product_id;
      const response = await leftoverService.getByParentId(parentId);
      setLeftovers(response.leftovers || []);
    } catch (err) {
      console.error('Failed to fetch leftovers:', err);
      setLeftoverError('获取余料库存失败: ' + (err.message || '未知错误'));
    } finally {
      setLeftoverLoading(false);
    }
  }, []);

  const handleLeftoverDialogClose = useCallback(() => {
    setLeftoverDialog({ open: false, product: null });
    setLeftovers([]);
    setLeftoverError('');
  }, []);

  return (
    <AccessibilityWrapper>
      <Layout title="库存管理" activeTab="inventory">
        <div className="container mx-auto px-4 py-6 max-w-7xl" role="main">
          {error && (
            <Alert
              variant="destructive"
              className="mb-4"
              role="alert"
              aria-live="assertive"
            >
              <AlertDescription className="flex items-center justify-between">
                {error}
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setError('')}>
                  <X className="h-4 w-4" />
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Client Context Selector */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-4">
                {selectedClientId === 'global' ? (
                  <Globe className="h-5 w-5 text-primary" />
                ) : (
                  <Building2 className="h-5 w-5 text-muted-foreground" />
                )}
                <h2 className="text-lg font-semibold">库存视图上下文</h2>
              </div>

              <ClientSelector
                selectedClientId={selectedClientId}
                onClientChange={handleClientChange}
                showGlobalOption={true}
                helperText={
                  selectedClientId === 'global'
                    ? '显示所有客户的库存数据'
                    : `显示客户 ${selectedClient?.name || selectedClientId} 的专属库存数据`
                }
              />

              {selectedClientId !== 'global' && selectedClient && (
                <div className="mt-4 p-3 bg-primary/10 border border-primary/20 rounded-md">
                  <p className="text-sm text-primary flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    <strong>客户上下文:</strong> {selectedClient.name}
                    {selectedClient.client_code && ` (${selectedClient.client_code})`}
                    - 显示该客户的专属产品属性、定价和库存数据
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stock Type Tile Map */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card
              className={`cursor-pointer transition-all hover:shadow-md ${
                viewMode === 'finished'
                  ? 'ring-2 ring-primary bg-primary/5'
                  : 'hover:bg-muted/50'
              }`}
              onClick={() => handleViewModeChange('finished')}
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  viewMode === 'finished' ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'
                }`}>
                  <Package className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">成品库存</p>
                  <p className="text-2xl font-bold">{stats?.finished_stock_total ?? '-'}</p>
                  <p className="text-xs text-muted-foreground">件</p>
                </div>
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all hover:shadow-md ${
                viewMode === 'raw_material'
                  ? 'ring-2 ring-primary bg-primary/5'
                  : 'hover:bg-muted/50'
              }`}
              onClick={() => handleViewModeChange('raw_material')}
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  viewMode === 'raw_material' ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'
                }`}>
                  <Ruler className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">原材料库存</p>
                  <p className="text-2xl font-bold">{stats?.raw_material_total ?? '-'}</p>
                  <p className="text-xs text-muted-foreground">根</p>
                </div>
              </CardContent>
            </Card>

            <Card
              className={`cursor-pointer transition-all hover:shadow-md ${
                viewMode === 'leftover'
                  ? 'ring-2 ring-primary bg-primary/5'
                  : 'hover:bg-muted/50'
              }`}
              onClick={() => handleViewModeChange('leftover')}
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  viewMode === 'leftover' ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'
                }`}>
                  <Recycle className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">余料库存</p>
                  <p className="text-2xl font-bold">{stats?.leftover_total ?? '-'}</p>
                  <p className="text-xs text-muted-foreground">根</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Optimized Inventory Overview - only for finished goods */}
          {viewMode === 'finished' && (
            <InventoryOverview
              stats={stats}
              onClearAllFilters={handleClearAllFilters}
              onSetFilter={setFilters}
              onSetSearchMode={setSearchMode}
              onSetAdvancedFiltersOpen={setAdvancedFiltersOpen}
              onFilterSubmit={handleFilterSubmit}
              onExportInventory={handleExportInventory}
            />
          )}

          {/* Enhanced Search & Filters */}
          <div className="flex justify-end items-center gap-2 mb-2">
            <Switch
              id="exclude-zero-stock-page"
              checked={excludeZeroStock}
              onCheckedChange={setExcludeZeroStock}
            />
            <Label htmlFor="exclude-zero-stock-page" className="text-sm cursor-pointer">隐藏零库存</Label>
          </div>
          <AdvancedSearch
            searchMode={searchMode}
            filters={filters}
            categories={categories}
            searchSuggestions={searchSuggestions}
            searchLoading={searchLoading}
            globalSearch={globalSearch}
            onSearchModeToggle={handleSearchModeToggle}
            onFiltersChange={handleFilterChange}
            onAdvancedFilterChange={handleAdvancedFilterChange}
            onGlobalSearchChange={handleGlobalSearchChange}
            onFilterSubmit={handleFilterSubmit}
            onClearFilter={handleClearFilter}
            onClearAllFilters={handleClearAllFilters}
            onExport={handleExportInventory}
            advancedFiltersOpen={advancedFiltersOpen}
            setAdvancedFiltersOpen={setAdvancedFiltersOpen}
            loading={loading}
            searchPlaceholder="按名称、编号、位置或规格搜索库存..."
            pageType="inventory"
            viewMode={viewMode}
          />

          {/* Inventory Actions - placeholder for future action buttons */}
          <div className="flex justify-end gap-2 mb-4">
            {/* Stock changes should only happen through warehouse inbound/outbound movements */}
          </div>

          {/* Responsive Inventory List */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  {viewMode === 'leftover' ? (
                    <LeftoverInventoryList
                      inventory={inventory}
                      loading={loading}
                      sort={sort}
                      onSort={handleSort}
                      onClearAllFilters={handleClearAllFilters}
                      debouncedGlobalSearch={debouncedGlobalSearch}
                      onViewLeftover={(item) => {
                        // Open leftover dialog for the selected item
                        setLeftoverDialog({ open: true, product: {
                          product_code: item.parent_product_code || item.parent_product_id,
                          name: item.parent_product_name
                        } });
                        setLeftoverLoading(true);
                        setLeftoverError('');
                        leftoverService.getByParentId(item.parent_product_code || item.parent_product_id)
                          .then(response => setLeftovers(response.leftovers || []))
                          .catch(err => setLeftoverError('获取余料库存失败: ' + (err.message || '未知错误')))
                          .finally(() => setLeftoverLoading(false));
                      }}
                    />
                  ) : (
                    <InventoryList
                      inventory={inventory}
                      loading={loading}
                      sort={sort}
                      onSort={handleSort}
                      onClearAllFilters={handleClearAllFilters}
                      debouncedGlobalSearch={debouncedGlobalSearch}
                      onViewHistory={handleViewHistory}
                      onViewLeftovers={handleViewLeftovers}
                      onRefresh={loadInventory}
                      selectedClientId={selectedClientId}
                      selectedClient={selectedClient}
                    />
                  )}

                  {pagination.total_pages > 1 && (
                    <div className="flex justify-center py-4 border-t">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page <= 1}
                        >
                          上一页
                        </Button>
                        <span className="text-sm text-muted-foreground px-4">
                          第 {pagination.page} / {pagination.total_pages} 页
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page >= pagination.total_pages}
                        >
                          下一页
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <p
            className="mt-4 text-sm text-muted-foreground"
            role="status"
            aria-live="polite"
            aria-label={`显示 ${inventory.length} / ${pagination.total_count} 个库存项目`}
          >
            显示 {inventory.length} / {pagination.total_count} 个
            {viewMode === 'finished' ? '成品库存' : viewMode === 'raw_material' ? '原材料库存' : '余料库存'}
            项目
          </p>

          {/* Inventory History Dialog */}
          <InventoryHistoryDialog
            open={historyDialog.open}
            onClose={handleHistoryDialogClose}
            productId={historyDialog.product?.product_id || historyDialog.product?.product_code}
            productInfo={historyDialog.product}
            selectedClientId={selectedClientId}
          />

          <Dialog open={leftoverDialog.open} onOpenChange={handleLeftoverDialogClose}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
              <DialogHeader className="pb-4 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Recycle className="h-5 w-5 text-primary" />
                    <DialogTitle>余料库存</DialogTitle>
                  </div>
                  <Badge variant="outline">
                    {leftovers.length} 条余料
                  </Badge>
                </div>
              </DialogHeader>

              <div className="flex-1 overflow-auto py-4">
                {leftoverLoading ? (
                  <div className="flex justify-center items-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
                    <span>加载余料库存...</span>
                  </div>
                ) : leftoverError ? (
                  <div className="text-center py-8 text-destructive">
                    {leftoverError}
                  </div>
                ) : leftovers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Recycle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>该产品暂无余料库存</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {leftovers.map((leftover) => (
                      <div
                        key={leftover._id}
                        className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <span className="font-medium text-sm">{leftover.leftover_code}</span>
                            <Badge variant="secondary" className="ml-2 text-xs">
                              {leftover.status === 'available' ? '可用' : leftover.status === 'consumed' ? '已消耗' : leftover.status}
                            </Badge>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {leftover.length}mm
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="text-muted-foreground">
                            顶径: {leftover.tip_diameter}mm
                          </div>
                          <div className="text-muted-foreground">
                            数量: {leftover.quantity || 1}
                          </div>
                          <div className="text-muted-foreground">
                            位置: {leftover.location || '-'}
                          </div>
                          <div className="text-muted-foreground">
                            创建: {new Date(leftover.created_at).toLocaleDateString('zh-CN')}
                          </div>
                        </div>
                        {leftover.comments && (
                          <p className="text-xs text-muted-foreground mt-2 border-t pt-2">
                            {leftover.comments}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <DialogFooter className="border-t pt-4">
                <Button variant="outline" onClick={handleLeftoverDialogClose}>
                  关闭
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Success/Error Notifications */}
          {notification.open && (
            <div className="fixed bottom-4 right-4 z-50">
              <Alert
                variant={notification.severity === 'error' ? 'destructive' : 'default'}
                className="w-auto shadow-lg"
              >
                <AlertDescription className="flex items-center gap-2">
                  {notification.message}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => setNotification(prev => ({ ...prev, open: false }))}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </AlertDescription>
              </Alert>
            </div>
          )}
        </div>
      </Layout>
    </AccessibilityWrapper>
  );
};

export default InventoryPage;
